import * as Sentry from '@sentry/react';
import type { APIError } from '@vizit/api';
import { useBulkImportCsvMutation } from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import React from 'react';

interface UseProductBulkImportDialogProps {
  /**
   * The ID of the portfolio to import products to
   */
  portfolioId?: string;
  /**
   * Callback when bulk import is successful
   */
  onSuccessfulImport?: (count: number) => void;
  /**
   * Callback when an error occurs during import
   */
  onImportError?: (error: unknown | APIError) => void;
}

interface UseProductBulkImportDialogReturn {
  /**
   * Whether the bulk import is currently in progress
   */
  isImporting: boolean;
  /**
   * Submit handler for the bulk import dialog
   * @param file - The CSV file to import
   */
  onSubmit: (file: File) => Promise<void>;
}

const logBulkImportError = (error: unknown, portfolioId?: string, fileName?: string) => {
  Sentry.addBreadcrumb({
    message: 'Bulk product import failed.',
    level: 'error',
    data: { portfolio_id: portfolioId, file_name: fileName },
  });

  Sentry.captureException(error, {
    level: 'error',
    tags: {
      feature: SENTRY_FEATURE_TAGS.Product,
      stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Product].bulkImport,
    },
    extra: {
      portfolio_id: portfolioId,
      file_name: fileName,
    },
  });
};

/**
 * Hook for handling product bulk import dialog logic
 */
function useProductBulkImportDialog(props: UseProductBulkImportDialogProps): UseProductBulkImportDialogReturn {
  const { portfolioId, onSuccessfulImport, onImportError } = props;
  const toast = useToast();

  const { mutateAsync, isPending: isImporting } = useBulkImportCsvMutation();

  const handleOnSuccessfulImport = React.useCallback(
    (count: number) => {
      toast.addToast({
        title: 'Importing your products now!',
        description: 'We will email you when all products are finished processing and available for optimization.',
        level: AlertLevel.Info,
      });
      onSuccessfulImport?.(count);
    },
    [onSuccessfulImport, toast],
  );

  const handleOnImportError = React.useCallback(
    (error: unknown) => {
      toast.addToast({
        title: 'Bulk import failed',
        description: 'An error occurred while importing the file. Please try again.',
        level: AlertLevel.Error,
      });
      onImportError?.(error);
    },
    [onImportError, toast],
  );

  const handleOnSubmit = React.useCallback(
    async (file: File) => {
      Sentry.addBreadcrumb({
        message: 'Starting bulk product import.',
        level: 'info',
        data: { portfolio_id: portfolioId, file_name: file.name, file_size: file.size },
      });

      try {
        const responseData = await mutateAsync({
          portfolio_id: portfolioId ?? '',
          file: file,
        });

        if (responseData?.id) {
          Sentry.addBreadcrumb({
            message: 'Bulk product import initiated successfully.',
            level: 'info',
            data: {
              bulk_import_id: responseData.id,
              pdps_created: responseData.pdps_created,
              pdps_skipped: responseData.pdps_skipped,
              total_pdps: responseData.total_pdps,
              portfolio_id: portfolioId,
            },
          });

          // Call success callback with the number of PDPs created
          handleOnSuccessfulImport(responseData.pdps_created);
        } else {
          throw new Error(`Failed to create bulk import: ${JSON.stringify({ portfolio_id: portfolioId })}`);
        }
      } catch (error) {
        logBulkImportError(error, portfolioId, file.name);
        handleOnImportError(error);
        throw error;
      }
    },
    [portfolioId, handleOnSuccessfulImport, handleOnImportError, mutateAsync],
  );

  return {
    isImporting,
    onSubmit: handleOnSubmit,
  };
}

export { useProductBulkImportDialog };
export type { UseProductBulkImportDialogProps, UseProductBulkImportDialogReturn };
